import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import paletteOutline from '../../../assets/images/palette-outline.svg'
import Loader from '../shared/Loader'
import {
  ExportHeader,
  CollectInfo,
  ErrorAlert,
  CustomExportForm,
  useExportPage
} from '../shared/Export'
import { addFormInputs, getDateRangeParams, splitSelection } from '../ExportModal/utils/exportHelpers'
import { formatDate } from '../../utils/dateHelpers'

const EMPTY_PREFERENCES = {}

const photoSections = sections => sections
  .map(section => ({
    ...section,
    questions: section.questions.filter(question => question.context === 'photos')
  }))
  .filter(section => section.questions.length > 0)

const PhotosExport = ({
  collectId,
  draftId = null,
  currentSectionId = null,
  returnUrl = null,
  savedPreferences = EMPTY_PREFERENCES,
  ignoreFilters = false
}) => {
  const buildInitialSelection = useCallback(sections => {
    if (!currentSectionId)
      return null

    const selection = {}
    sections.forEach(section => {
      const questionIds = section.questions.map(question => question.id)
      selection[section.id] = section.id === currentSectionId ? questionIds : []
    })
    return selection
  }, [currentSectionId])

  const {
    loading,
    error,
    data,
    ensemble,
    selectedQuestions,
    setSelectedQuestions,
    totalSelectedQuestions
  } = useExportPage({
    collectId,
    preferencesKey: 'photos',
    savedPreferences,
    transformSections: photoSections,
    buildInitialSelection
  })

  const getRedirectBackUrl = useCallback(() => {
    if (returnUrl)
      return returnUrl
    if (draftId)
      return `/collects/${collectId}/drafts/${draftId}/photo_gallery`
    return `/collects/${collectId}/drafts/`
  }, [collectId, draftId, returnUrl])

  const handleExport = useCallback(() => {
    const form = document.getElementById('export-form')
    const { sectionIds, questionIds } = splitSelection(selectedQuestions)

    // Clear existing hidden inputs
    form.querySelectorAll('input[type="hidden"]:not([name="authenticity_token"])').forEach(input => {
      input.remove()
    })

    sectionIds.forEach(sectionId => addFormInputs(form, { 'section_ids[]': sectionId }))
    questionIds.forEach(questionId => addFormInputs(form, { 'question_ids[]': questionId }))
    addFormInputs(form, {
      // eslint-disable-next-line camelcase
      return_url: returnUrl ?? null,
      // eslint-disable-next-line camelcase
      ignore_filters: ignoreFilters ? 'true' : null,
      ...getDateRangeParams()
    })

    if (window.plausible)
      window.plausible('Collect Photos Exported')

    form.submit()
  }, [selectedQuestions, returnUrl, ignoreFilters])

  const handleSubmit = useCallback(event => {
    event.preventDefault()
    handleExport()
  }, [handleExport])

  const getExportUrl = useCallback(() => {
    const baseUrl = `/collects/${collectId}`
    return draftId ? `${baseUrl}/drafts/${draftId}/photos_exports` : `${baseUrl}/photos_exports`
  }, [collectId, draftId])

  if (loading)
    return <Loader text="Chargement des données..." size="small" />

  if (error)
    return <ErrorAlert message={error} />

  if (!data)
    return null
  const currentDraft = data.drafts.find(draftItem => draftItem.id === draftId)
  const startingAt = currentDraft?.starting_at ? formatDate(currentDraft.starting_at) : null

  return (
    <div className="modal-content border">
      <ExportHeader
        title="Personnaliser l'export photos"
        icon={paletteOutline}
        iconAlt="Customize"
        cancelUrl={getRedirectBackUrl()}
      />
      <div className="modal-body">
        <CollectInfo
          collectName={data.name}
          startingAt={startingAt}
          ensembleName={ensemble?.name}
        />
        <CustomExportForm
          exportUrl={getExportUrl()}
          sections={data.sections}
          selectedQuestions={selectedQuestions}
          title="Sélectionnez les sections et questions photos à exporter"
          onQuestionChange={setSelectedQuestions}
          onSubmit={handleSubmit}
        />
      </div>
      <div className="modal-footer">
        <div className="d-flex justify-content-end align-items-center w-100">
          <button
            type="submit"
            form="export-form"
            className="btn btn-success"
            disabled={totalSelectedQuestions === 0}
          >
            Exporter les photos
          </button>
        </div>
      </div>
    </div>
  )
}

PhotosExport.propTypes = {
  collectId: PropTypes.number.isRequired
}

export default PhotosExport
